import { ProductionRoutePeriodCoverage } from '@/hooks/api/v2/productionRoutes'
import { Button } from '@/common/components/Button/Button'
import { ShareArrowIcon } from '@/common/components/icons'
import PeriodCoverageStatusBadge from './PeriodCoverageStatusBadge'
import {
  Table,
  TableCell,
  TableColumnHeader,
  TableFooterCell,
  TableRow
} from '@/common/components/Table/Table'
import useI18n from '@/shared/hooks/useI18n'
import { UnitLabel } from '@/shared/config/config'
import { Card, CardBody } from '@/common'

export default function ProductionRoutePeriodSummary({
  finalizedReportId,
  finalizedReportStatus,
  reportingPeriodBasis,
  periodCoverage,
  activityLevel,
  specificEmbeddedDirect,
  specificEmbeddedIndirect,
  beginFinalization,
  viewFinalizedReport,
  onMouseEnter,
  onMouseLeave
}: {
  finalizedReportId: string | null
  finalizedReportStatus: 'PENDING' | 'SUBMITTED' | null
  reportingPeriodBasis: string
  periodCoverage: ProductionRoutePeriodCoverage[]
  activityLevel: number
  specificEmbeddedDirect: number
  specificEmbeddedIndirect: number
  beginFinalization: () => void
  viewFinalizedReport: (finalizedReportId: string) => void
  onMouseEnter: (period: string) => void
  onMouseLeave: () => void
}) {
  const { numberFormatter3Decimals, t } = useI18n()
  const formattedTotalDirect = specificEmbeddedDirect
    ? `${numberFormatter3Decimals(specificEmbeddedDirect * activityLevel)} ${UnitLabel.tCO2e}`
    : '-'
  const formattedTotalIndirect = specificEmbeddedIndirect
    ? `${numberFormatter3Decimals(specificEmbeddedIndirect * activityLevel)} ${UnitLabel.tCO2e}`
    : '-'

  const formattedActivityLevel = activityLevel ? `${activityLevel} ${UnitLabel.t}` : '-'
  const formattedSpecificEmbeddedDirect = specificEmbeddedDirect
    ? `${numberFormatter3Decimals(specificEmbeddedDirect)} ${UnitLabel.tCO2ePerT}`
    : '-'
  const formattedSpecificEmbeddedIndirect = specificEmbeddedIndirect
    ? `${numberFormatter3Decimals(specificEmbeddedIndirect)} ${UnitLabel.tCO2ePerT}`
    : '-'

  const isFinalizeEnabled = periodCoverage.every(period => period.reportSubmitted)

  const onFinalizePeriodClick = () => {
    if (finalizedReportId) {
      return viewFinalizedReport(finalizedReportId)
    }
    beginFinalization()
  }

  return (
    <Card>
      <CardBody className="border-b">
        <div className="flex xl:items-center xl:justify-between gap-4 flex-col xl:flex-row">
          <div className="grid gap-2 grid-cols-2 sm:grid-cols-4 lg:grid-cols-12">
            {periodCoverage.map(period => (
              <PeriodCoverageStatusBadge
                key={period.month}
                onMouseEnter={() => onMouseEnter(period.month)}
                onMouseLeave={onMouseLeave}
                reportingPeriodBasis={reportingPeriodBasis}
                periodCoverage={period}
              />
            ))}
          </div>
          <div className="self-end">
            <Button
              disabled={!isFinalizeEnabled}
              sizeType="sm"
              icon={<ShareArrowIcon />}
              onClick={onFinalizePeriodClick}
            >
              {finalizedReportId &&
                finalizedReportStatus === 'PENDING' &&
                t('reports:productionRoutePeriodSummary.completeFinalization')}
              {finalizedReportId &&
                finalizedReportStatus === 'SUBMITTED' &&
                t('reports:productionRoutePeriodSummary.viewFinalizedReport')}
              {!finalizedReportId && t('reports:productionRoutePeriodSummary.beginFinalization')}
            </Button>
          </div>
        </div>
      </CardBody>
      <div className="flex flex-col lg:flex-row p-4 gap-4">
        <div className="shrink-0">
          <Table className="h-full">
            <thead>
              <TableRow>
                <TableColumnHeader colSpan={2} className="text-right">
                  {t('reports:productionRoutePeriodSummary.totalSubmittedReports')}
                </TableColumnHeader>
              </TableRow>
            </thead>
            <tbody>
              <TableRow>
                <TableCell>{t('reports:activityLevel')}</TableCell>
                <TableCell className="text-right">{formattedActivityLevel}</TableCell>
              </TableRow>
            </tbody>
            <tfoot className="h-full">
              <TableRow>
                <TableFooterCell colSpan={2} />
              </TableRow>
            </tfoot>
          </Table>
        </div>
        <div className="flex-grow">
          <Table>
            <thead>
              <TableRow>
                <TableColumnHeader className="text-right" colSpan={2}>
                  {t('reports:productionRoutePeriodSummary.direct')}
                </TableColumnHeader>
                <TableColumnHeader className="text-right">
                  {t('reports:productionRoutePeriodSummary.indirect')}
                </TableColumnHeader>
              </TableRow>
            </thead>
            <tbody>
              <TableRow className="text-right">
                <TableCell>
                  {t('reports:productionRoutePeriodSummary.specificEmbeddedEmissions')}
                </TableCell>
                <TableCell>{formattedSpecificEmbeddedDirect}</TableCell>
                <TableCell>{formattedSpecificEmbeddedIndirect}</TableCell>
              </TableRow>
            </tbody>
            <tfoot>
              <TableRow className="text-right">
                <TableFooterCell>{t('reports:productionRoutePeriodSummary.total')}</TableFooterCell>
                <TableFooterCell>{formattedTotalDirect}</TableFooterCell>
                <TableFooterCell>{formattedTotalIndirect}</TableFooterCell>
              </TableRow>
            </tfoot>
          </Table>
        </div>
      </div>
    </Card>
  )
}
